// The adapter from ACP to the transcript's items: a session's transcript, as
// the server stores it and its socket streams it (api/transcript.ts), folded
// into the items today's Code tab drew (types.ts). It reads every payload as
// untrusted. Nothing here reads the page or the server, and nothing a
// session writes shows raw.
//
// A runner's item is what its agent said, as ACP wrote it
// (crates/poulpe-runner/src/acp.rs): the params of a `session/update`, a
// turn's end (`{stopReason}`), a turn's error (`{error}`) or the runner's
// own notice (`{notice}`). A sent item is a prompt someone sent
// (`{prompt, by}`, and `question` when it carries an answer, or `message` when it
// carries another lead session's message, `by` that session's agent: Poulpe-774). Poulpe's item
// says that Poulpe resumed the session after its runner restarted
// (`{notice, resumed: "after_restart"}`): the turn that ran then, if one did,
// ends there, its calls stopped, and is not sent again. Or it says how one of
// the session's proposals ended (`{notice, proposal, ended}`, Poulpe-565), or
// that a prompt that waited for its resume was not sent (`{notice,
// undelivered: true}`, Poulpe-604): a notice that ends nothing. The server
// keeps an item over 1 MiB as a marker of its size, and marks how many
// updates the runner dropped while it was away
// (crates/poulpe-server/src/transcript.rs). The agents' shapes are those of
// their adapters, claude-agent-acp 0.94 and codex-acp 2.2, for a client
// that presents forms and nothing else (dev/acp).
//
// How each kind becomes an item:
// - `agent_message_chunk` and `agent_thought_chunk`: a reply and a thought.
//   Chunks that follow one another join while they are of one message; a
//   chunk that is not text is skipped. The last text of a turn under way is
//   still being written. A notice the agent says as text is a notice:
//   Claude's marks it (`_meta.claudeCode.kind: "informational"`), Codex's
//   is a warning or a compaction, said outside any message. A sub-agent's
//   text (`_meta.claudeCode.parentToolUseId`) is not the lead's reply, and
//   is kept in the child’s transcript.
// - `tool_call` and `tool_call_update`: one tool call, where it started,
//   which each update changes; an update of a call not seen yet starts it
//   there.
//   - Its name is Claude's (`_meta.claudeCode.toolName`), an MCP tool's
//     `mcp__<server>__<tool>`, or what its ACP kind says (`execute` is a
//     command, `edit` an edit, `read` a read).
//   - A command's line is its input's `command`, without the login shell
//     Codex wraps it in.
//   - Its diffs (`{type: "diff", path, oldText, newText}`) are unified diffs,
//     one per file: Claude's own patch when its tool's response holds one
//     (`_meta.claudeCode.toolResponse.structuredPatch`), with its lines'
//     numbers; else the lines that changed between the old and the new
//     text, which name no line numbers, since the text may be a part of
//     its file.
//   - Its output, once it has ended, is the text its content held when it
//     ended, without the fence Claude wraps a command's output in: until
//     then Claude's content only says again what the call was given. Else
//     it is what Codex's terminal said (`_meta.terminal_output_delta`,
//     joined), which also shows while the call runs, or its raw output.
//   - Its images are its content's image blocks.
//   - It runs under the sub-agent's call Claude names
//     (`_meta.claudeCode.parentToolUseId`).
//   - Claude's word that it was not run, for a person, a rule or a mode
//     refused it, makes it denied.
//   - A compaction of the conversation (Claude's `compact` call, Codex's
//     "Compact conversation") is a notice.
// - `plan`: the turn's plan, one per turn, where it was first sent; each
//   update replaces its entries, until the next prompt. A sub-agent's plan
//   is not the turn's, and is skipped.
// - A prompt: who sent it and its words; one that carries an answer to the
//   session's question was given on the board, and names the question.
// - A turn's end: how long it took from its prompt, and why it stopped. A
//   turn's error, and the runner's notice: a notice.
// - The server's markers: a notice that says what is missing; a prompt's, a
//   prompt that says it was too large.
// - Native children (`subagent_update`, or Codex’s `subagent_spawned` and
//   `subagent_state_update`) hold their own fold, fed by their ACP session id.
//   Legacy Agent/Task calls retain their nested output for kept transcripts.
// - `user_message_chunk` (the prompt said back), `usage_update`,
//   `current_mode_update`, `config_option_update`, `available_commands_update`
//   and `session_info_update`: no item. Today's transcript showed none of
//   them: the strip above it, and the composer, did.
import type { Actor, SentFile, TranscriptItem } from '../api/types'
import { childSettings, launchSettings, type EngineSettings } from './agentSettings'
import type { FileEdit, ImageData, Item, NoticeItem, NoticeLevel, PlanItem, PlanStepStatus, ToolItem, ToolStatus } from './types'

// --- reading a payload, as untrusted ---------------------------------------------------------

type Json = Record<string, unknown>

const isRecord = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value)
const record = (value: unknown): Json => (isRecord(value) ? value : {})
const text = (value: unknown): string | null => (typeof value === 'string' ? value : null)
const count = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null)

/** The files a sent prompt carried, as the server writes them; what is not one is left out. */
function filesOf(value: unknown): SentFile[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((file): SentFile[] => {
    if (!isRecord(file)) return []
    const name = text(file.name)
    const bytes = count(file.bytes)
    return name !== null && bytes !== null ? [{ name, mime_type: text(file.mime_type) ?? '', bytes, ...(text(file.id) ? { id: text(file.id)! } : {}) }] : []
  })
}

/** An actor, as the server writes one; null when it is not. */
export function actorOf(value: unknown): Actor | null {
  if (!isRecord(value)) return null
  switch (value.kind) {
    case 'person':
      return typeof value.member === 'string' ? { kind: 'person', member: value.member } : null
    case 'agent': {
      const agent = value.agent
      if (!isRecord(agent) || typeof agent.vendor !== 'string' || typeof agent.model !== 'string' || typeof value.session !== 'string') return null
      return { kind: 'agent', agent: { vendor: agent.vendor, model: agent.model }, session: value.session }
    }
    case 'poulpe':
      return { kind: 'poulpe' }
    default:
      return null
  }
}

/** Claude's own fields of an update: `_meta.claudeCode`. */
const claudeOf = (update: Json): Json => record(record(update._meta).claudeCode)

// --- words for what the server kept out ------------------------------------------------------

const DECIMAL = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const WHOLE = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })

/** A size, in bytes, as people read one: `2.1 MB`, `340 kB`, `12 bytes`. */
export function sizeText(bytes: number): string {
  if (bytes >= 1_000_000) return `${DECIMAL.format(bytes / 1_000_000)} MB`
  if (bytes >= 1_000) return `${WHOLE.format(bytes / 1_000)} kB`
  return `${WHOLE.format(bytes)} ${bytes === 1 ? 'byte' : 'bytes'}`
}

// --- a tool call ------------------------------------------------------------------------------

type AcpStatus = 'pending' | 'in_progress' | 'completed' | 'failed'
const ACP_STATUSES: readonly AcpStatus[] = ['pending', 'in_progress', 'completed', 'failed']
const ended = (status: AcpStatus) => status === 'completed' || status === 'failed'

/** What the adapter holds of one tool call between its updates: each field as the last update that set it said it. */
export interface Call {
  /** The call's id, as its agent gave it. */
  readonly id: string
  /** Where its item is. */
  readonly index: number
  readonly title: string | null
  readonly kind: string | null
  /** The `name` some agents give a call beside ACP's fields. */
  readonly name: string | null
  readonly status: AcpStatus
  readonly rawInput: unknown
  readonly rawOutput: unknown
  readonly content: readonly unknown[]
  /** Whether its content came with its end or its tool's response: what it gave back, not what it was given said again. */
  readonly answered: boolean
  readonly locations: readonly unknown[]
  /** Claude's name of its tool. */
  readonly toolName: string | null
  /** Claude's sub-agent call it runs under. */
  readonly parent: string | null
  /** Claude's patch of the file it changed, from its tool's response: `{filePath, structuredPatch}`. */
  readonly patch: Json | null
  /** The files it changed, as its content and its patch say: made again only when either changes. */
  readonly edits: FileEdit[]
  /** Claude's word that it was not run because someone, a rule or a mode refused it. */
  readonly refused: boolean
  /** Claude's compaction's facts: `{trigger, preTokens, postTokens?}`. */
  readonly compaction: Json | null
  /** Codex's MCP call: its input is `{server, tool, arguments}`. */
  readonly mcp: boolean
  /** What Codex's terminal, or its MCP tool, said while it ran, joined. */
  readonly terminal: string
  /** Whether its turn was stopped, or failed, before it said it had ended: it runs no more, unless a later update says so. */
  readonly cut: boolean
}

/** A call before its first update. */
function newCall(id: string, index: number): Call {
  return {
    id,
    index,
    title: null,
    kind: null,
    name: null,
    status: 'pending',
    rawInput: undefined,
    rawOutput: undefined,
    content: [],
    answered: false,
    locations: [],
    toolName: null,
    parent: null,
    patch: null,
    edits: [],
    refused: false,
    compaction: null,
    mcp: false,
    terminal: '',
    cut: false,
  }
}

/**
 * Whether Claude says a call was not run because it was refused: by the
 * person (`user-rejected`), a rule (`permission-rule`) or its mode
 * (`automode-…`), or by a decision its response names. A call stopped
 * before it ran, by an interrupt, failed.
 */
function refusedBy(claude: Json): boolean {
  const kind = text(claude.nonExecutionKind)
  if (kind === 'user-rejected' || kind === 'permission-rule' || kind?.startsWith('automode')) return true
  return typeof record(claude.toolResponse).decisionReasonType === 'string'
}

/** A call with an update's fields applied. A field the update does not set, or sets to null, stays as it was. */
function updated(call: Call, update: Json): Call {
  const next: { -readonly [K in keyof Call]: Call[K] } = { ...call }
  const title = text(update.title)
  if (title !== null) next.title = title
  const kind = text(update.kind)
  if (kind !== null) next.kind = kind
  const name = text(update.name)
  if (name !== null) next.name = name
  if (ACP_STATUSES.includes(update.status as AcpStatus)) next.status = update.status as AcpStatus
  if (update.rawInput !== undefined && update.rawInput !== null) next.rawInput = update.rawInput
  if (update.rawOutput !== undefined && update.rawOutput !== null) next.rawOutput = update.rawOutput
  const claude = claudeOf(update)
  const response = record(claude.toolResponse)
  if (Array.isArray(update.content)) {
    next.content = update.content
    next.answered = ended(next.status) || 'toolResponse' in claude
  }
  if (Array.isArray(update.locations)) next.locations = update.locations
  const toolName = text(claude.toolName)
  if (toolName) next.toolName = toolName
  const parent = text(claude.parentToolUseId)
  if (parent) next.parent = parent
  if (Array.isArray(response.structuredPatch) && response.structuredPatch.length > 0) next.patch = response
  if (next.content !== call.content || next.patch !== call.patch) next.edits = editsOf(next.content, next.patch)
  if (refusedBy(claude)) next.refused = true
  if (next.toolName === 'compact' && isRecord(update.rawOutput)) next.compaction = update.rawOutput
  const meta = record(update._meta)
  if (meta.is_mcp_tool_call === true) next.mcp = true
  for (const delta of [meta.terminal_output_delta, meta.mcp_output_delta]) {
    const data = text(record(delta).data)
    if (data) next.terminal += data
  }
  return next
}

// --- a command's line -------------------------------------------------------------------------

/**
 * The one word a shell reads in `line`, its quotes taken off as the shell
 * takes them; null when it is not exactly one word. Nothing in it is
 * expanded: it is read, not run.
 */
function shellWord(line: string): string | null {
  let word = ''
  let at = 0
  while (at < line.length) {
    const c = line[at]!
    if (c === "'") {
      const end = line.indexOf("'", at + 1)
      if (end < 0) return null
      word += line.slice(at + 1, end)
      at = end + 1
    } else if (c === '"') {
      at += 1
      for (;;) {
        if (at >= line.length) return null
        const d = line[at]!
        if (d === '"') {
          at += 1
          break
        }
        // In double quotes a backslash escapes only these.
        if (d === '\\' && at + 1 < line.length && '$`"\\\n'.includes(line[at + 1]!)) {
          word += line[at + 1]
          at += 2
          continue
        }
        word += d
        at += 1
      }
    } else if (c === '\\') {
      if (at + 1 >= line.length) return null
      word += line[at + 1]
      at += 2
    } else if (/\s/.test(c)) {
      return null
    } else {
      word += c
      at += 1
    }
  }
  return word
}

const SHELL = /^(?:\/(?:usr\/)?bin\/)?(?:ba|z)?sh$/
const LOGIN_SHELL = /^(?:\/(?:usr\/)?bin\/)?(?:ba|z)?sh -l?c[ \t]+([\s\S]+)$/

/**
 * A command's line as it was meant: Codex runs a command in a login shell,
 * `/bin/zsh -lc "<command>"`, and the command alone reads better. Anything
 * else stays as it is.
 */
export function commandLine(command: string): string {
  const wrapped = LOGIN_SHELL.exec(command.trim())
  if (!wrapped) return command
  return shellWord(wrapped[1]!.trim()) ?? command
}

/** The command an input names: its line, or its words joined. */
function commandOf(input: Json): string | null {
  const said = input.command
  if (typeof said === 'string' && said.trim() !== '') return commandLine(said)
  if (Array.isArray(said) && said.length > 0 && said.every((word) => typeof word === 'string')) {
    const words = said as string[]
    // `["bash", "-lc", "<command>"]`: the command alone.
    if (words.length === 3 && SHELL.test(words[0]!) && /^-l?c$/.test(words[1]!)) return words[2]!
    return words.join(' ')
  }
  return null
}

// --- diffs ------------------------------------------------------------------------------------

/** A text's lines, without the empty one after its last line break. */
function linesOf(said: string): string[] {
  if (said === '') return []
  const lines = said.split('\n')
  if (lines.at(-1) === '') lines.pop()
  return lines
}

export type DiffOp = { kind: ' ' | '-' | '+'; text: string }

/** Past this many cells, the lines between what both texts keep at their ends are shown as removed, then added, rather than compared. */
const COMPARE_CELLS = 2_000_000

/** The lines that turn `before` into `after`, by their longest common subsequence: kept, removed and added, in order. */
export function lineDiff(before: readonly string[], after: readonly string[]): DiffOp[] {
  let head = 0
  while (head < before.length && head < after.length && before[head] === after[head]) head++
  let tail = 0
  while (tail < before.length - head && tail < after.length - head && before[before.length - 1 - tail] === after[after.length - 1 - tail]) tail++
  const a = before.slice(head, before.length - tail)
  const b = after.slice(head, after.length - tail)
  const middle: DiffOp[] = []
  if (a.length * b.length > COMPARE_CELLS) {
    for (const line of a) middle.push({ kind: '-', text: line })
    for (const line of b) middle.push({ kind: '+', text: line })
  } else {
    // longest[i * width + j]: how many lines a[i..] and b[j..] have in common, in order.
    const width = b.length + 1
    const longest = new Uint32Array((a.length + 1) * width)
    for (let i = a.length - 1; i >= 0; i--) {
      for (let j = b.length - 1; j >= 0; j--) {
        longest[i * width + j] = a[i] === b[j] ? longest[(i + 1) * width + j + 1]! + 1 : Math.max(longest[(i + 1) * width + j]!, longest[i * width + j + 1]!)
      }
    }
    let i = 0
    let j = 0
    while (i < a.length || j < b.length) {
      if (i < a.length && j < b.length && a[i] === b[j]) {
        middle.push({ kind: ' ', text: a[i]! })
        i++
        j++
      } else if (i < a.length && (j >= b.length || longest[(i + 1) * width + j]! >= longest[i * width + j + 1]!)) {
        // What goes is said before what comes, as a unified diff has it.
        middle.push({ kind: '-', text: a[i]! })
        i++
      } else {
        middle.push({ kind: '+', text: b[j]! })
        j++
      }
    }
  }
  const kept = (line: string): DiffOp => ({ kind: ' ', text: line })
  return [...before.slice(0, head).map(kept), ...middle, ...after.slice(after.length - tail).map(kept)]
}

/** The lines of context a hunk keeps around what changed, as `diff -u` does. */
const CONTEXT = 3

/**
 * The hunks of a unified diff between the old and the new text of a part of
 * a file, with three lines of context. Where that part stands in its file is
 * not known, so a hunk's header names no line numbers: `@@`.
 */
export function hunksOf(before: string, after: string): string[] {
  const ops = lineDiff(linesOf(before), linesOf(after))
  const changed = ops.flatMap((op, at) => (op.kind === ' ' ? [] : [at]))
  if (changed.length === 0) return []
  const hunks: string[] = []
  let from = Math.max(0, changed[0]! - CONTEXT)
  let to = Math.min(ops.length, changed[0]! + CONTEXT + 1)
  const flush = () => hunks.push(['@@', ...ops.slice(from, to).map((op) => `${op.kind}${op.text}`)].join('\n'))
  for (const at of changed.slice(1)) {
    if (at - CONTEXT <= to) {
      to = Math.min(ops.length, at + CONTEXT + 1)
      continue
    }
    flush()
    from = at - CONTEXT
    to = Math.min(ops.length, at + CONTEXT + 1)
  }
  flush()
  return hunks
}

/** A new file's diff: every line added, from its first. */
function addedFile(after: string): string {
  const lines = linesOf(after)
  return [`@@ -0,0 +1,${lines.length} @@`, ...lines.map((line) => `+${line}`)].join('\n')
}

/** A deleted file's diff: every line removed. */
function deletedFile(before: string): string {
  const lines = linesOf(before)
  return [`@@ -1,${lines.length} +0,0 @@`, ...lines.map((line) => `-${line}`)].join('\n')
}

/** Claude's structured patch as a unified diff: its hunks, with their lines' numbers. */
function patchDiff(hunks: readonly unknown[]): string | null {
  const said: string[] = []
  for (const hunk of hunks) {
    if (!isRecord(hunk) || !Array.isArray(hunk.lines)) return null
    const numbers = [hunk.oldStart, hunk.oldLines, hunk.newStart, hunk.newLines].map(count)
    if (numbers.some((n) => n === null)) return null
    const [oldStart, oldLines, newStart, newLines] = numbers as number[]
    said.push(`@@ -${oldStart},${oldLines} +${newStart},${newLines} @@`)
    for (const line of hunk.lines) if (typeof line === 'string') said.push(line)
  }
  return said.length > 0 ? said.join('\n') : null
}

/**
 * The files a call changed, each as one unified diff, in the order its
 * content names them: Claude's own patch of its file, with its lines'
 * numbers, in the place of what its content said of that file.
 */
function editsOf(content: readonly unknown[], patch: Json | null): FileEdit[] {
  const patchPath = patch ? text(patch.filePath) : null
  const patchedDiff = patch && patchPath !== null ? patchDiff(patch.structuredPatch as unknown[]) : null
  // A patch that cannot be read leaves the file to what the content says of it.
  const patched = patchedDiff === null ? null : patchPath
  const byPath = new Map<string, string[]>()
  for (const entry of content) {
    if (!isRecord(entry) || entry.type !== 'diff') continue
    const path = text(entry.path)
    const after = text(entry.newText)
    if (path === null || after === null) continue
    if (path === patched) {
      // Its place is kept: the patch takes it below.
      byPath.set(path, byPath.get(path) ?? [])
      continue
    }
    const before = text(entry.oldText)
    const kind = text(record(entry._meta).kind)
    const parts = before === null || kind === 'add' ? [addedFile(after)] : kind === 'delete' ? [deletedFile(before)] : hunksOf(before, after)
    byPath.set(path, [...(byPath.get(path) ?? []), ...parts])
  }
  if (patched !== null && patchedDiff !== null) byPath.set(patched, [patchedDiff])
  return [...byPath].flatMap(([path, parts]) => (parts.length > 0 ? [{ path, diff: parts.join('\n') }] : []))
}

// --- what a call gave back --------------------------------------------------------------------

/** A text without the fence it is wrapped in, when it is all one fenced block. */
export function unfenced(said: string): string {
  const firstBreak = said.indexOf('\n')
  if (firstBreak < 0) return said
  const marker = said[0]
  if (marker !== '`' && marker !== '~') return said
  let width = 0
  while (said[width] === marker) width++
  if (width < 3 || said.slice(width, firstBreak).includes('`')) return said

  let end = said.length
  while (said[end - 1] === ' ' || said[end - 1] === '\t') end--
  let close = end
  while (close > firstBreak && said[close - 1] === marker) close--
  if (end - close < width || said[close - 1] !== '\n') return said
  return said.slice(firstBreak + 1, Math.max(firstBreak + 1, close - 1))
}

/** The text of a content block, `{type: "text", text}`; null for any other. */
function blockText(block: unknown): string | null {
  return isRecord(block) && block.type === 'text' ? text(block.text) : null
}

/** What a call's raw output says as text: a string, its content blocks' text, an MCP result's or its error; null when it says nothing to read. */
function rawText(raw: unknown): string | null {
  if (typeof raw === 'string') return raw
  if (Array.isArray(raw)) {
    const texts = raw.flatMap((block) => blockText(block) ?? [])
    return texts.length > 0 ? texts.join('\n') : null
  }
  if (!isRecord(raw)) return null
  const error = raw.error
  if (typeof error === 'string' && error !== '') return error
  if (isRecord(error) && typeof error.message === 'string') return error.message
  const result = record(raw.result)
  if (Array.isArray(result.content)) return rawText(result.content)
  if (Array.isArray(raw.content)) return rawText(raw.content)
  return null
}

/**
 * What a call gave back, as text to read; null while it has said nothing.
 * Once it has ended: the text its content held when it ended, else its raw
 * output (an MCP tool's result or error), else what its terminal said.
 */
function outputOf(call: Call): string | null {
  let said: string | null = null
  if (ended(call.status)) {
    const texts = call.answered ? call.content.flatMap((entry) => (isRecord(entry) && entry.type === 'content' ? (blockText(entry.content) ?? []) : [])) : []
    const raw = rawText(call.rawOutput)
    said = texts.length > 0 ? unfenced(texts.join('\n')) : raw !== null && raw.trim() !== '' ? raw : call.terminal
  } else said = call.terminal
  return said === null || said.trim() === '' ? null : said
}

/**
 * The images a call's content holds, as the agent sent them: ACP's image
 * block, `{type: "image", mimeType, data}`, as Claude's Read of an image
 * gives it (fixtures/claude.json), or Claude's own API's,
 * `{type: "base64", media_type, data}`, should its adapter pass one on so.
 */
function imagesOf(call: Call): ImageData[] {
  return call.content.flatMap((entry): ImageData[] => {
    if (!isRecord(entry) || entry.type !== 'content') return []
    const block = record(entry.content)
    const data = text(block.data)
    const mime = block.type === 'image' ? text(block.mimeType) : block.type === 'base64' ? text(block.media_type) : null
    return data && mime ? [{ mime, data }] : []
  })
}

// --- the call as the transcript shows it -----------------------------------------------------

/** What an ACP kind names a call that its agent gives no name of its own. */
const KIND_NAMES: Record<string, string> = {
  execute: 'Command',
  edit: 'Edit',
  read: 'Read',
  search: 'Search',
  fetch: 'Fetch',
  delete: 'Delete',
  move: 'Move',
  think: 'Think',
  switch_mode: 'Mode',
}

/** An MCP call's server and tool, from Codex's input, `{server, tool, arguments}`. */
function mcpOf(call: Call): { server: string; tool: string; args: unknown } | null {
  const input = record(call.rawInput)
  const server = text(input.server)
  const tool = text(input.tool)
  if (!server || !tool || !(call.mcp || 'arguments' in input)) return null
  return { server, tool, args: input.arguments }
}

/** The first path a call's locations name. */
function locationOf(call: Call): string | null {
  for (const location of call.locations) {
    const path = text(record(location).path)
    if (path) return path
  }
  return null
}

/** Whether a call is the agent compacting its conversation: Claude's `compact`, Codex's "Compact conversation". */
const isCompaction = (call: Call) => call.toolName === 'compact' || (call.toolName === null && call.kind === 'think' && call.title === 'Compact conversation')

const TOKENS = new Intl.NumberFormat('en-GB')

/** A compaction as today's notice said it: when it happened, and whether it was asked for. */
function compactionNotice(call: Call, base: { id: string; at: string }): NoticeItem {
  if (!ended(call.status)) return { ...base, kind: 'notice', level: 'info', text: 'Compacting the conversation…' }
  if (call.status === 'failed') return { ...base, kind: 'notice', level: 'warn', text: 'The conversation could not be compacted.' }
  const facts = record(call.compaction)
  const before = count(facts.preTokens)
  return {
    ...base,
    kind: 'notice',
    level: 'info',
    text: `The conversation was compacted${before ? ` at ${TOKENS.format(before)} tokens` : ''}${facts.trigger === 'manual' ? ', as asked' : ''}.`,
  }
}

const STATUS: Record<AcpStatus, ToolStatus> = { pending: 'running', in_progress: 'running', completed: 'ok', failed: 'error' }

/** A call as the transcript shows it, from all its updates so far. */
function toolOf(call: Call, base: { id: string; at: string }): ToolItem | NoticeItem {
  if (isCompaction(call)) return compactionNotice(call, base)
  const mcp = mcpOf(call)
  const kindName = call.kind && Object.hasOwn(KIND_NAMES, call.kind) ? KIND_NAMES[call.kind] : null
  const name = call.toolName ?? (mcp ? `mcp__${mcp.server}__${mcp.tool}` : null) ?? kindName ?? call.name ?? call.title ?? 'Tool'
  const input = mcp ? mcp.args : call.rawInput
  // Without an input, what a call is about is the file it read, or its title.
  const subject = input === undefined ? (locationOf(call) ?? (call.title && call.title !== name ? call.title : null)) : null
  const command = !mcp && call.kind === 'execute' ? commandOf(record(call.rawInput)) : null
  const status: ToolStatus = call.status === 'failed' && call.refused ? 'denied' : !ended(call.status) && call.cut ? 'stopped' : STATUS[call.status]
  const { edits } = call
  // An edit's diff is what it did: its tool's word that it did says nothing more. Its failure does.
  const said = edits.length > 0 && call.status !== 'failed' ? null : outputOf(call)
  const images = imagesOf(call)
  return {
    ...base,
    kind: 'tool',
    tool_use_id: call.id,
    name,
    input,
    ...(subject ? { subject } : {}),
    command,
    edits,
    status,
    output: said === null ? null : { text: said },
    parent: call.parent,
    ...(images.length > 0 ? { images } : {}),
  }
}

// --- the session's folder ---------------------------------------------------------------------

/** The folder the runner makes for a session, `…/sessions/<session id>` (crates/poulpe-runner/src/worktree.rs). */
const SESSION_FOLDER = /^(\/.*?\/sessions\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/

/** A path without the slashes it ends with, but for its first character: `/a//` is `/a`, `//` is `/`. A loop, as a pattern would take quadratic time on a long run of slashes. */
function withoutTrailingSlashes(path: string): string {
  let end = path.length
  while (end > 1 && path[end - 1] === '/') end -= 1
  return path.slice(0, end)
}

/** The session's folder, as a call shows it: Codex names it as its command's; Claude's paths are under it. */
function folderOf(call: Call): string | null {
  const input = record(call.rawInput)
  const cwd = text(input.cwd)
  if (cwd && cwd.startsWith('/')) return withoutTrailingSlashes(cwd)
  const paths = [text(input.file_path), text(input.path), ...call.locations.map((location) => text(record(location).path)), ...call.content.map((entry) => text(record(entry).path))]
  for (const path of paths) {
    const found = path ? SESSION_FOLDER.exec(path) : null
    if (found) return found[1]!
  }
  return null
}

// --- what one item says -----------------------------------------------------------------------

/** What one item says, as the fold takes it. */
type Said =
  | { kind: 'text' | 'thought'; text: string; message: string | null }
  | { kind: 'tool'; call: string; update: Json }
  | { kind: 'plan'; entries: PlanItem['entries'] }
  | { kind: 'prompt'; text: string; by: Actor; question: string | null; message: string | null; opening: boolean; files: SentFile[] }
  | { kind: 'prompt_omitted'; bytes: number | null; by: Actor | null }
  | { kind: 'end'; reason: string }
  | { kind: 'failed'; text: string }
  | { kind: 'notice'; level: NoticeLevel; text: string }
  | { kind: 'resumed'; text: string }

const PLAN_STATUSES: readonly PlanStepStatus[] = ['pending', 'in_progress', 'completed']

/** Codex's own notices, said as text outside any message (codex-acp's warning and compaction events). */
const CODEX_NOTICE = /^(?:(?<warn>(?:Config warning|Warning): )|\*(?<info>Context compacted[^*\n]*)\*\s*$)/

/** Claude's word for a notice's level: `**Warning:** …`. */
const LEVEL_HEAD = /^\*\*([A-Za-z ]+):\*\*\s*/

/** A chunk of text that is a notice, not the agent's reply; null for a reply. */
function noticeOfChunk(update: Json, said: string): Said | null {
  const claude = claudeOf(update)
  if (claude.kind === 'informational') {
    const level: NoticeLevel = claude.level === 'error' ? 'error' : claude.level === 'warning' ? 'warn' : 'info'
    const head = LEVEL_HEAD.exec(said)
    return { kind: 'notice', level, text: (head ? `${head[1]}: ${said.slice(head[0].length)}` : said).trim() }
  }
  if (update.messageId !== undefined && update.messageId !== null) return null
  const codex = CODEX_NOTICE.exec(said)
  if (!codex) return null
  return codex.groups?.warn ? { kind: 'notice', level: 'warn', text: said.trim() } : { kind: 'notice', level: 'info', text: codex.groups?.info?.trim() ?? said.trim() }
}

/** What a `session/update` says; null for what shows nothing. */
function saidOfUpdate(update: Json, child = false): Said | null {
  switch (update.sessionUpdate) {
    case 'session_message': {
      const content = Array.isArray(update.content) ? update.content.flatMap(block => blockText(block) ?? []).join('\n') : ''
      return child && content ? {kind: 'prompt', text: content, by: {kind: 'poulpe'}, question: null, message: null, opening: false, files: []} : null
    }
    case 'user_message_chunk':
      return child && blockText(update.content) ? {kind: 'prompt', text: blockText(update.content)!, by: {kind: 'poulpe'}, question: null, message: null, opening: false, files: []} : null
    case 'agent_message_chunk':
    case 'agent_thought_chunk': {
      const said = blockText(update.content)
      if (!said) return null
      // A sub-agent's words are its own, not the lead's reply.
      if (!child && text(claudeOf(update).parentToolUseId)) return null
      if (update.sessionUpdate === 'agent_message_chunk') {
        const notice = noticeOfChunk(update, said)
        if (notice) return notice
      }
      return { kind: update.sessionUpdate === 'agent_message_chunk' ? 'text' : 'thought', text: said, message: text(update.messageId) }
    }
    case 'tool_call':
    case 'tool_call_update': {
      const call = text(update.toolCallId)
      return call ? { kind: 'tool', call, update } : null
    }
    case 'plan': {
      if (!Array.isArray(update.entries) || (!child && text(claudeOf(update).parentToolUseId))) return null
      const entries = update.entries.flatMap((entry): PlanItem['entries'] => {
        if (!isRecord(entry) || typeof entry.content !== 'string') return []
        const status = PLAN_STATUSES.includes(entry.status as PlanStepStatus) ? (entry.status as PlanStepStatus) : 'pending'
        return [{ content: entry.content, status }]
      })
      return { kind: 'plan', entries }
    }
    default:
      return null
  }
}

/** A turn's end, its error, or the runner's notice, as a runner's payload says it; null when it says none. */
function endOf(payload: Json): Said | null {
  if (typeof payload.notice === 'string') return { kind: 'notice', level: 'info', text: payload.notice }
  if (typeof payload.stopReason === 'string') return { kind: 'end', reason: payload.stopReason }
  if ('error' in payload) {
    const error = payload.error
    const message = isRecord(error) ? text(error.message) : text(error)
    return { kind: 'failed', text: message ? `The turn failed: ${message}` : 'The turn failed.' }
  }
  return null
}

/** What the server's marker says: what is missing, then the turn's end or failure it kept. */
function saidOfMarker(source: TranscriptItem['source'], payload: Json): Said[] {
  const omitted = record(payload.omitted)
  if (source === 'sent') return [{ kind: 'prompt_omitted', bytes: count(omitted.bytes), by: actorOf(payload.by) }]
  const updates = count(omitted.updates)
  if (updates !== null && !('bytes' in omitted)) {
    const what = updates === 1 ? 'An update is' : `${WHOLE.format(updates)} updates are`
    return [{ kind: 'notice', level: 'warn', text: `${what} missing here: the runner dropped ${updates === 1 ? 'it' : 'them'} while the server was away.` }]
  }
  const bytes = count(omitted.bytes)
  const notice: Said = { kind: 'notice', level: 'info', text: `Part of the transcript was too large to show${bytes === null ? '' : ` (${sizeText(bytes)})`}.` }
  const end = endOf(payload)
  return end ? [notice, end] : [notice]
}

/** What Poulpe says of the session: that it resumed it after a restart, how one of its proposals ended (Poulpe-565), or that a prompt that waited for its resume was not sent (Poulpe-604); nothing else. */
function saidOfPoulpe(payload: Json): Said[] {
  if (payload.resumed === 'after_restart') return [{ kind: 'resumed', text: text(payload.notice) ?? 'The session was resumed after a restart.' }]
  const notice = text(payload.notice)
  if (notice !== null && payload.undelivered === true) return [{ kind: 'notice', level: 'warn', text: notice }]
  if (notice === null || text(payload.proposal) === null) return []
  return [{ kind: 'notice', level: payload.ended === 'landed' ? 'info' : 'warn', text: notice }]
}

/** What an item says, in order: most say one thing, a marker two at most, and what shows nothing nothing. */
function saidOf(item: TranscriptItem, child: boolean): Said[] {
  const { payload } = item
  if (!isRecord(payload)) return []
  if (isRecord(payload.omitted)) return saidOfMarker(item.source, payload)
  if (item.source === 'poulpe') return saidOfPoulpe(payload)
  if (item.source === 'sent') {
    const prompt = text(payload.prompt)
    const by = actorOf(payload.by)
    return prompt !== null && by ? [{ kind: 'prompt', text: prompt, by, question: text(payload.question), message: text(payload.message), opening: payload.opening === true, files: filesOf(payload.attachments) }] : []
  }
  const said = isRecord(payload.update) ? saidOfUpdate(payload.update, child) : endOf(payload)
  return said ? [said] : []
}

// --- folding ----------------------------------------------------------------------------------

/** An engine child: its own ACP stream, or a kept Agent/Task call. */
export interface EngineAgent {
  readonly id: string
  readonly at: string
  readonly name: string
  readonly state: 'working' | 'waiting' | 'completed' | 'failed' | 'stopped' | 'unknown'
  readonly canStop: boolean
  readonly settings?: EngineSettings
  readonly launches?: readonly string[]
  readonly legacy?: boolean
  /** A legacy launch returned before its child ended. */
  readonly background?: boolean
  readonly output?: string | null
  readonly fold: AcpFold
}

/** A gap loses live observations, not terminal evidence. Recurse through every child. */
function unknownChildren(agents: ReadonlyMap<string, EngineAgent>): ReadonlyMap<string, EngineAgent> {
  let result: Map<string, EngineAgent> | null = null
  for (const [id, agent] of agents) {
    const children = unknownChildren(agent.fold.subagents)
    const state = agent.state === 'working' || agent.state === 'waiting' ? 'unknown' : agent.state
    if (state !== agent.state || children !== agent.fold.subagents) {
      result ??= new Map(agents)
      result.set(id, {...agent, state, fold: children === agent.fold.subagents ? agent.fold : {...agent.fold, subagents: children}})
    }
  }
  return result ?? agents
}

/** The transcript read so far and the state needed to fold its next updates. */
export interface AcpFold {
  readonly subagents: ReadonlyMap<string, EngineAgent>
  readonly items: readonly Item[]
  /** Each tool call, by its id. */
  readonly calls: ReadonlyMap<string, Call>
  /** The message the last item's text is of, when it is text: a chunk of another one starts another item. */
  readonly message: string | null
  /** Where this turn's plan is, until its end. */
  readonly plan: number | null
  /** When this turn's prompt was sent; null between turns. A turn's end measures from it. */
  readonly turn: string | null
  /** The session's folder, once a call shows it: paths under it show from there. */
  readonly cwd: string | null
  /** Where the items after the last turn's end begin: a stopped or failed turn cuts the calls from there. */
  readonly since: number
}

export const EMPTY_FOLD: AcpFold = { subagents: new Map(), items: [], calls: new Map(), message: null, plan: null, turn: null, cwd: null, since: 0 }

const isText = (item: Item | undefined): item is Extract<Item, { kind: 'assistant' | 'thinking' }> => item?.kind === 'assistant' || item?.kind === 'thinking'

/** How long from one moment to another, in milliseconds; null when either is not a time. */
function between(from: string | null, to: string): number | null {
  if (from === null) return null
  const span = Date.parse(to) - Date.parse(from)
  return Number.isFinite(span) && span >= 0 ? span : null
}

/**
 * The fold once these items, which follow those it holds, are in. An item
 * that nothing changed is the object it was, and the fold itself is when
 * nothing changed: what draws an item draws it again only when it changed.
 */
export function foldAcp(fold: AcpFold, items: readonly TranscriptItem[], inChild = false): AcpFold {
  let list = fold.items as Item[]
  let calls = fold.calls as Map<string, Call>
  let { message, plan, turn, cwd, since, subagents } = fold
  let copied = false
  let changed = false
  const write = () => {
    if (copied) return
    list = list.slice()
    calls = new Map(calls)
    copied = true
  }
  /** Whatever comes after a text ends its writing. */
  const push = (item: Item): number => {
    write()
    const last = list.at(-1)
    if (isText(last) && last.streaming) list[list.length - 1] = { ...last, streaming: false }
    list.push(item)
    message = null
    return list.length - 1
  }
  /**
   * A stopped or failed turn's calls that had not said they ended run no
   * more, as ACP has a client take them once it cancels. A later update
   * that says how one ended still has the last word. The calls of earlier
   * turns are left running: Claude's commands moved to the background go on
   * after their turn.
   */
  const cut = (stopChildren = true) => {
    const running = [...calls.values()].filter((call) => call.index >= since && !ended(call.status) && !call.cut)
    if (running.length === 0) return
    write()
    for (const call of running) {
      const stopped = { ...call, cut: true }
      calls.set(call.id, stopped)
      const before = list[call.index]!
      list[call.index] = toolOf(stopped, { id: before.id, at: before.at })
      const key = `tool:${call.id}`
      const child = subagents.get(key)
      if (child && stopChildren) subagents = new Map(subagents).set(key, {...child, state: 'stopped'})
    }
  }
  /** A turn's end: what comes next is another turn's, or none's. */
  const endTurn = () => {
    turn = null
    plan = null
    since = list.length
  }

  for (const item of items) {
    try {
      fold1(item)
    } catch {
      // An update the adapter cannot take does not stop the transcript: it says so in its place, and the next ones go on.
      changed = true
      push({ id: `${item.id}/unshown`, at: item.at, kind: 'notice', level: 'warn', text: 'An update could not be shown.' })
    }
  }
  return changed ? { items: list, calls, message, plan, turn, cwd, since, subagents } : fold

  function fold1(item: TranscriptItem) {
    const payload = record(item.payload)
    const owner = text(payload.sessionId)
    // A native child's whole stream stays in its own fold, including nested agents.
    const owns = (agent: EngineAgent, id: string): boolean => agent.id === id || [...agent.fold.subagents.values()].some(child => owns(child, id))
    const child = owner === null ? undefined : [...subagents.values()].find(agent => !agent.legacy && owns(agent, owner))
    if (child) {
      const next = foldAcp(child.fold, [item], true)
      const update = record(payload.update)
      const settings = owner === child.id ? childSettings(child.settings, update) : child.settings
      if (next !== child.fold || settings !== child.settings || update.sessionUpdate === 'session_message') {
        const launch = owner === child.id && update.sessionUpdate === 'session_message' ? text(update.messageId) : null
        const launches = launch && !child.launches?.includes(launch) ? [...(child.launches ?? []), launch] : child.launches
        subagents = new Map(subagents).set(child.id, { ...child, settings, launches, fold: next })
        changed = true
      }
      return
    }
    const omitted = record(payload.omitted)
    // Size markers lose the owner, but keep the update kind. Oversized text or
    // tool output says nothing about child state; only missing lifecycle data does.
    const lostLifecycle = (count(omitted.updates) ?? 0) > 0
      || ['subagent_update', 'subagent_state_update', 'subagent_spawned'].includes(text(omitted.sessionUpdate) ?? '')
    if (item.source !== 'sent' && lostLifecycle) {
      const next = unknownChildren(subagents)
      if (next !== subagents) { subagents = next; changed = true }
    }
    const update = record(payload.update)
    const kind = update.sessionUpdate
    const parent = text(claudeOf(update).parentToolUseId)
    if (!inChild && parent) {
      const key = `tool:${parent}`
      const before = subagents.get(key) ?? {id: key, at: item.at, name: 'Untitled agent', state: 'working' as const, canStop: false, legacy: true, fold: EMPTY_FOLD}
      const cleaned = {...update, _meta: {...record(update._meta), claudeCode: {...claudeOf(update), parentToolUseId: null}}}
      subagents = new Map(subagents).set(key, {...before, fold: foldAcp(before.fold, [{...item, payload: {...payload, update: cleaned}}], true)})
      changed = true
      if (kind !== 'tool_call' && kind !== 'tool_call_update') return
    }
    if (kind === 'subagent_update' || kind === 'subagent_spawned' || kind === 'subagent_state_update') {
      const id = text(kind === 'subagent_update' ? update.sessionId : update.subagentSessionId)
      if (!id || id === owner) return
      const before = subagents.get(id)
      if (!before && kind === 'subagent_state_update') return
      let name = before?.name ?? 'Untitled agent'
      if ('description' in update || 'title' in update || 'task' in update || 'name' in update) {
        name = text(update.description) || text(update.task) || text(update.title) || text(update.name) || 'Untitled agent'
      }
      let state = before?.state ?? (kind === 'subagent_spawned' ? 'working' : 'unknown')
      if ('state' in update) {
        const snapshot = record(update.state)
        const value = typeof update.state === 'string' ? update.state : snapshot.state
        const reason = record(snapshot.stopReason).stopReason ?? record(snapshot.stopReason).reason ?? snapshot.stopReason
        state = value === 'running' ? 'working' : value === 'requires_action' ? 'waiting' : value === 'cancelled' || reason === 'cancelled' ? 'stopped' : value === 'failed' || reason === 'error' ? 'failed' : value === 'completed' || (value === 'idle' && typeof reason === 'string') ? 'completed' : 'unknown'
      }
      const canStop = 'capabilities' in update ? isRecord(record(update.capabilities).cancel) : before?.canStop ?? false
      let childFold = before?.fold ?? EMPTY_FOLD
      if ((state === 'working' || state === 'waiting') && childFold.turn === null) childFold = {...childFold, turn: item.at}
      if (before?.state !== state && (state === 'completed' || state === 'failed' || state === 'stopped')) {
        const payload = state === 'failed' ? {error: 'The subagent failed.'} : {stopReason: state === 'stopped' ? 'cancelled' : 'end_turn'}
        childFold = foldAcp(childFold, [{...item, id: `${item.id}/end`, payload}], true)
      }
      subagents = new Map(subagents).set(id, { id, at: before?.at ?? item.at, name, state, canStop, settings: before?.settings, launches: before?.launches, fold: childFold })
      changed = true
      return
    }
    saidOf(item, inChild).forEach((said, n) => {
      // An item that says two things keys the second apart.
      const base = { id: n === 0 ? item.id : `${item.id}/${n}`, at: item.at }
      changed = true
      switch (said.kind) {
        case 'text':
        case 'thought': {
          const kind = said.kind === 'text' ? 'assistant' : 'thinking'
          const last = list.at(-1)
          if (last?.kind === kind && (message === null || said.message === null || message === said.message)) {
            write()
            list[list.length - 1] = { ...last, text: last.text + said.text }
            message ??= said.message
          } else {
            push({ ...base, kind, text: said.text, streaming: turn !== null })
            message = said.message
          }
          break
        }
        case 'tool': {
          const known = calls.get(said.call)
          write()
          if (known) {
            const call = updated(known, said.update)
            calls.set(said.call, call)
            const before = list[call.index]!
            list[call.index] = toolOf(call, { id: before.id, at: before.at })
            cwd ??= folderOf(call)
          } else {
            const call = updated(newCall(said.call, list.length), said.update)
            push(toolOf(call, base))
            calls.set(said.call, call)
            cwd ??= folderOf(call)
          }
          const call = calls.get(said.call)!
          if (call.toolName === 'Task' || call.toolName === 'Agent' || call.name === 'Task' || call.name === 'Agent' || call.title === 'spawnAgent' || call.name === 'spawn_agent') {
            const key = `tool:${call.id}`
            const before = subagents.get(key)
            const input = record(call.rawInput)
            const shown = toolOf(call, base)
            const status = shown.kind === 'tool' ? shown.status : 'error'
            const output = shown.kind === 'tool' ? shown.output?.text : null
            const childFold = before?.fold ?? EMPTY_FOLD
            const response = record(claudeOf(said.update).toolResponse)
            const background = before?.background || input.run_in_background === true || response.isAsync === true || response.status === 'async_launched' || call.name === 'spawn_agent' || call.title === 'spawnAgent'
            // Completing a detached launch proves nothing about the child's end.
            // A later metadata-only patch must not revive state lost at a restart.
            const state = before && !ACP_STATUSES.includes(said.update.status as AcpStatus)
              ? background && status === 'ok' ? 'unknown' : before.state
              : said.update.status === 'in_progress' || said.update.status === 'pending' || status === 'running' ? 'working'
                : status === 'ok' ? background ? 'unknown' : 'completed' : status === 'stopped' ? 'stopped' : 'failed'
            subagents = new Map(subagents).set(key, {
              id: key, at: before?.at ?? item.at,
              name: text(input.description) || text(input.prompt) || before?.name || 'Untitled agent',
              state, background,
              canStop: false, legacy: true, output, fold: childFold,
              settings: launchSettings(before?.settings, input, record(claudeOf(said.update).toolResponse)),
            })
          }
          break
        }
        case 'plan': {
          const known = plan === null ? undefined : list[plan]
          if (known?.kind === 'plan') {
            write()
            list[plan!] = { ...known, entries: said.entries }
          } else {
            plan = push({ ...base, kind: 'plan', entries: said.entries })
          }
          break
        }
        // A prompt sent while a turn runs is steered into it, or waits for its end (crates/poulpe-runner/src/acp.rs): the turn
        // goes on, with its plan and its start. The turn a waiting prompt starts after that end shows no start of its own.
        case 'prompt':
          push({ ...base, kind: 'user', text: said.text, by: said.by, ...(said.question !== null ? { via: 'board' as const, question: said.question } : {}), ...(said.message !== null ? { message: said.message } : {}), ...(said.opening ? { opening: true as const } : {}), ...(said.files.length > 0 ? { files: said.files } : {}) })
          turn ??= item.at
          break
        case 'prompt_omitted':
          push({ ...base, kind: 'user', text: '', by: said.by ?? { kind: 'poulpe' }, omitted: { bytes: said.bytes, known: said.by !== null } })
          turn ??= item.at
          break
        case 'end':
          if (said.reason === 'cancelled') cut()
          push({ ...base, kind: 'result', duration_ms: between(turn, item.at), stop_reason: said.reason })
          endTurn()
          break
        case 'failed':
          // A turn's error ends it.
          cut()
          push({ ...base, kind: 'notice', level: 'error', text: said.text })
          endTurn()
          break
        case 'notice':
          push({ ...base, kind: 'notice', level: said.level, text: said.text })
          break
        case 'resumed':
          // Its runner went away: no child end was observed. Later snapshots can restore state.
          cut(false)
          subagents = unknownChildren(subagents)
          push({ ...base, kind: 'notice', level: 'info', text: said.text })
          endTurn()
          break
      }
    })
  }
}

/** The items of a whole transcript. */
export function toItems(items: readonly TranscriptItem[]): readonly Item[] {
  return foldAcp(EMPTY_FOLD, items).items
}
